import {BreadCrumb} from "primereact/breadcrumb";
import Icon from "../../system/components/icon";
import PageHeaderButton from "../components/page-header-button";
import {Button} from "primereact/button";
import {TabPanel, TabView} from "primereact/tabview";
import TaskDialog from "../tasks/task-dialog";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslate} from "../../system/useTranslate";
import {useNavigate} from "react-router-dom";
import StrategyMap from "./tabs/strategy-map";
import MyGoals from "./tabs/my-goals";
import TeamGoals from "./tabs/team-goals";
import {setMessage, setWorkspaceMembership} from "../../system/app-slice";
import {useGoal} from "../../models/goal/use-goal";
import GoalSidebar from "./components/goal-sidebar";
import {useUpdateEffect} from "primereact/hooks";
import ProjectSidebar from "../projects/components/project-sidebar";
import GoalProjectDialog from "./components/goal-project-dialog";
import {useProject} from "../../models/project/use-project";
import SelectableTabsButton, {useSelectableTabs} from "../components/selectable-tabs-button";
import {useWorkspaceRole} from "../../system/use-workspace-role";
import {usePermission} from "../../system/usePermission";
import AiProjectCreationButton from "../../system/components/ai-project-creation-button";
import {GoalPeriodReviewBanner} from "./components/goal-period-review-dialog";

const MAX_ROOT_GOALS = 3;

function isMissionRootGoal(goalItem) {
    return !goalItem?.parentGoal?.id && !goalItem?.parentGoal?.documentId && !goalItem?.isOrphan;
}

function getMissionRootGoalsCount(goals = []) {
    return (goals || []).filter(isMissionRootGoal).length;
}

function isMissionRootGoalPayload(goalData = {}) {
    return !goalData.parentGoal && !goalData.parentGoal?.id && !goalData.parentGoal?.documentId && !goalData.isOrphan;
}

function Index(props) {

    const [activeIndex,setActiveIndex] = useState(0);
    const [breadCrumb,setBreadCrumbData] = useState([]);
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const [createNodeDialogVisible, setCreateNodeDialogVisible] = useState(false);
    const [selectedGoalData, setSelectedGoalData] = useState();
    const [selectedProjectData, setSelectedProjectData] = useState();
    const [pending, setPending] = useState();
    const translate = useTranslate();
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const goal = useGoal();
    const project = useProject();
    const {role: workspaceRole} = useWorkspaceRole();
    const isGuest = workspaceRole === "guest";
    const permission = usePermission();
    const canCreateGoal = permission.can("create-new-goals");
    const canCreateProject = permission.can("create-new-projects");

    const availableTabs = [
        { id: "strategy-map", label: "strategy-map", icon: "mi mi:graph_1", view:StrategyMap },
        { id: "my-goals", label: "my-goals", icon: "mi mi:person", view:MyGoals },
        { id: "team-goals", label: "team-goals", icon: "mi mi:groups", view:TeamGoals, disabled:false }];
    const tabs = isGuest ? availableTabs.slice(0, 1) : availableTabs;
    const {visibleTabs, selectedIds, toggleTab} = useSelectableTabs(tabs, "visible-tabs:goals", activeIndex, setActiveIndex);

    useEffect(()=>{
        setBreadCrumbData([
            {label:translate("goals"), command:()=>{navigate("/goals")}}
        ]);
    },[]);

    const createNode=(nodeData)=>{
        if (nodeData.type==="goal"){
            if (!permission.can("create-new-goals", true)) return;
            createGoal({...nodeData?.state, owner: nodeData?.state?.owner})
        }
        if (nodeData.type==="project"){
            if (!permission.can("create-new-projects", true)) return;
            createProject({
                ...nodeData?.state,
                owner: nodeData?.state?.owner,
                members: nodeData?.state?.members,
            })
        }
        setCreateNodeDialogVisible(false);
        setPending(undefined)
    }

    const createGoal = async (goalData)=>{
        if (isMissionRootGoalPayload(goalData)
            && getMissionRootGoalsCount(workspaceMembershipData?.workspace?.goals) >= MAX_ROOT_GOALS) {
            dispatch(setMessage({
                severity: "warn",
                content: translate("strategy-map.validation.max-root-goals"),
            }));
            return;
        }

        try {
            await goal.createGoal({...goalData, workspace:workspaceMembershipData?.workspace.id});
        } catch (error) {
            const message = String(error?.message || error?.code || "");
            dispatch(setMessage({
                severity: "warn",
                content: message.startsWith("subscription.error.") ? translate(message) : translate("strategy-map.validation.generic-error"),
            }));
            return;
        }
        let _goals = await goal.getGoals(workspaceMembershipData?.workspace.id);
        dispatch(setWorkspaceMembership({...workspaceMembershipData, workspace: {...workspaceMembershipData.workspace, goals:_goals}}));
        return _goals;
    }
    const createProject = async (projectData)=>{
        let _data = await project.createProject({...projectData, workspace:workspaceMembershipData?.workspace.id});
        let _projects = await project.getProjects(workspaceMembershipData?.workspace.id);
        dispatch(setWorkspaceMembership({...workspaceMembershipData, workspace: {...workspaceMembershipData.workspace, projects:_projects}}));
        return _projects;
    }
    const refreshProjects = async ()=>{
        let _projects = await project.getProjects(workspaceMembershipData?.workspace.id);
        dispatch(setWorkspaceMembership({...workspaceMembershipData, workspace: {...workspaceMembershipData.workspace, projects:_projects}}));
        return _projects;
    }

    useUpdateEffect(()=>{
        if (pending?.type==="project")setSelectedProjectData(pending?.data?.entity);
        if (pending?.type==="goal")setSelectedGoalData(pending?.data?.entity);
    },[pending])

    return(
        <div className="h-full overflow-hidden flex flex-column">
            <BreadCrumb separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>} className="bg-transparent border-none text-xs" model={breadCrumb}/>
            <PageHeaderButton
                title={props.name}
                description={props.description}
                end={(canCreateGoal || canCreateProject) && (
                    <div className="flex flex-wrap align-items-center gap-2">
                        {!isGuest && (canCreateGoal || canCreateProject) && (
                            <AiProjectCreationButton
                                initialType="goal"
                                context={{
                                    pageType: "goals",
                                    route: "/goals",
                                    contextVersion: 1,
                                }}
                                onCompleted={refreshProjects}
                            />
                        )}
                        <Button
                            icon={<Icon icon="mi mi:add" className="text-lg ml-1"/>}
                            className="text-xs gap-2 p-2 pl-3"
                            onClick={()=>{setCreateNodeDialogVisible(true)}}
                            label={translate("create")}
                        />
                    </div>
                )}
            />
            {!isGuest && <GoalPeriodReviewBanner/>}
            <div className="flex-1 min-h-0 flex flex-column relative">
                {!isGuest && <SelectableTabsButton tabs={tabs} selectedIds={selectedIds} onChange={toggleTab} className="absolute z-5" style={{top: ".45rem", left: ".75rem"}}/>}
                <TabView lazy activeIndex={activeIndex} onTabChange={(e)=>{setActiveIndex(e.index);
                    setBreadCrumbData(prv=>{
                        const next = Array.isArray(prv) ? [...prv] : [];
                        next[1] = {label:translate(visibleTabs[e.index].label)};
                        return next;
                    });
                }}
                         panelContainerClassName="flex-1  min-h-0 p-0 border-noround"
                         className="h-full flex flex-column">
                    {visibleTabs.map((tab, tabIndex) => {
                        const TabContent = tab.view;
                        return (
                        <TabPanel
                            key={tab.id}
                            header={translate(tab.label)}
                            disabled={tab.disabled}
                            leftIcon={<Icon icon={tab.icon} className="pl-2" />}
                            className="h-full text-xs">
                            {tabIndex === activeIndex && <div className="h-full min-h-0 overflow-hidden">
                                <TabContent setGoalDialogVisible={setCreateNodeDialogVisible} setPending={setPending} />
                            </div>}
                        </TabPanel>
                    )})}
                </TabView>
            </div>
            {!isGuest && (
                <GoalProjectDialog
                    visible={createNodeDialogVisible}
                    payload={pending}
                    singleTab={canCreateGoal && !canCreateProject
                        ? "goal"
                        : (!canCreateGoal && canCreateProject ? "project" : undefined)}
                    onSubmit={(e)=>{createNode(e)}}
                    onHide={()=>{setPending(undefined); setCreateNodeDialogVisible(false)}}
                />
            )}
            <GoalSidebar goalData={selectedGoalData} setGoalData={setSelectedGoalData} onHide={()=>{setPending(undefined)}}/>
            <ProjectSidebar projectData={selectedProjectData} setProjectData={setSelectedProjectData} onHide={()=>{setPending(undefined)}}/>
        </div>
    )
}



export default Index;
